import React, { useState, useEffect } from 'react';
import { Dropdown, Button } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import {
  coursePageAnswerValidationQuestion,
  coursePageRestartCourse,
} from 'dsoapi';

const CoursePageValidationQuestions = ({
  courseData,
  setPageValid,
  showValidationInfo,
}) => {
  const initialTimer =
    courseData.timedValidation?.secondsSinceStart > 90
      ? 0
      : 90 - courseData.timedValidation?.secondsSinceStart;
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answeredIncorrect, setAnsweredIncorrect] = useState(false);
  const [ranOutOfTime, setRanOutOfTime] = useState(initialTimer === 0);
  const [navigateCountdown, setNavigateCountdown] = useState(30);
  const [timer, setTimer] = useState(initialTimer);
  const navigate = useNavigate();

  useEffect(() => {
    if (courseData.timedValidation?.secondsSinceStart > 90) {
      coursePageRestartCourse(courseData.hashLink);
    }
  }, []);

  useEffect(() => {
    setPageValid(() => async () => {
      if (!selectedAnswer) {
        return false;
      }
      const request = {
        answer: selectedAnswer,
      };
      const response = await coursePageAnswerValidationQuestion(
        courseData.hashLink,
        request
      );
      const { answerIsCorrect } = response?.data || {};
      setAnsweredIncorrect(!answerIsCorrect);
      if (!answerIsCorrect) {
        coursePageRestartCourse(courseData.hashLink);
      }
      return answerIsCorrect;
    });
  }, [setPageValid, selectedAnswer]);

  useEffect(() => {
    let timerInterval;
    if (timer > 0) {
      timerInterval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            setRanOutOfTime(true);
            coursePageRestartCourse(courseData.hashLink);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [timer]);

  useEffect(() => {
    let timer;
    if ((answeredIncorrect || ranOutOfTime) && navigateCountdown > 0) {
      timer = setTimeout(() => {
        setNavigateCountdown(navigateCountdown - 1);
      }, 1000);
    } else if (navigateCountdown === 0) {
      navigate('/dashboard');
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [answeredIncorrect, ranOutOfTime, navigateCountdown]);

  const dropdownOptions =
    courseData.timedValidation?.answerOptions?.map((option) => ({
      text: option,
      value: option,
    })) || [];

  if (answeredIncorrect) {
    return (
      <div className="ui container">
        <div style={{ textAlign: 'center' }}>
          You answered incorrectly. The course will now restart from the
          beginning.
          <br />
          <br />
          You will be taken back to the dashboard in {navigateCountdown} second
          {navigateCountdown === 1 ? '' : 's'}.
        </div>
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Button primary size="large" onClick={() => navigate('/dashboard')}>
            <i className="home icon"></i>Return to Dashboard now
          </Button>
        </div>
      </div>
    );
  }

  if (ranOutOfTime) {
    return (
      <div className="ui container">
        <div style={{ textAlign: 'center' }}>
          You did not answer the validation question in time. The course will
          now restart from the beginning.
          <br />
          <br />
          You will be taken back to the dashboard in {navigateCountdown} second
          {navigateCountdown === 1 ? '' : 's'}.
        </div>
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Button primary size="large" onClick={() => navigate('/dashboard')}>
            <i className="home icon"></i>Return to Dashboard now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ui container" style={{ marginBottom: '400px' }}>
      <style>
        {`
          .validation-dropdown .menu {
            max-height: 50rem !important;
          }
          .flashing-emoji {
            animation: fadeInOut 1s ease-in-out infinite;
          }
          @keyframes fadeInOut {
            0% { opacity: 0.3; }
            50% { opacity: 1; }
            100% { opacity: 0.3; }
          }
        `}
      </style>
      <h4 className="ui dividing header">Identity Validation Question</h4>
      <div className="ui stackable grid">
        <div className="sixteen wide column">
          <p>
            Please answer the following validation question to confirm your
            identity. This should match the answer you provided when you first
            enrolled. You have{' '}
            <b>
              {timer <= 30 && <span className="flashing-emoji">🚨 </span>}
              {timer} second{timer === 1 ? '' : 's'}
              {timer <= 30 && <span className="flashing-emoji"> 🚨</span>}
            </b>
            &nbsp;to answer correctly.
          </p>
          <div className="field">
            <label htmlFor="validationQuestion">
              {courseData.timedValidation?.question ||
                'What is your favorite color?'}
            </label>
            <Dropdown
              name="validationQuestion"
              placeholder="Select your answer"
              fluid
              selection
              options={dropdownOptions}
              value={selectedAnswer}
              onChange={(_, { value }) => setSelectedAnswer(value)}
              className="validation-dropdown"
            />
            {showValidationInfo && !selectedAnswer && (
              <p className="error">Please select an answer to continue</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePageValidationQuestions;
